/**
 * OAuth2 Multi-Provider Authentication Service
 * Supports: Google and Email/Password
 */

import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut as firebaseSignOut,
  User as FirebaseUser,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import { auth } from "../config/firebase";

export interface AuthResult {
  user: FirebaseUser;
  idToken: string;
  provider: string;
  providerName: string;
}

export interface OAuthProvider {
  id: string;
  name: string;
  icon: string;
  color: string;
}

/**
 * Available OAuth providers
 */
export const OAUTH_PROVIDERS: Record<string, OAuthProvider> = {
  google: {
    id: "google.com",
    name: "Google",
    icon: "🔍",
    color: "#4285F4",
  },
};

/**
 * Initialize auth persistence
 */
export const initializeAuthPersistence = async (): Promise<void> => {
  try {
    await setPersistence(auth, browserLocalPersistence);
  } catch (error) {
    console.warn("Auth persistence initialization failed:", error);
  }
};

/**
 * Sign in with Google using popup
 */
export const signInWithGooglePopup = async (): Promise<AuthResult> => {
  try {
    const provider = new GoogleAuthProvider();
    provider.addScope("profile");
    provider.addScope("email");
    provider.setCustomParameters({ prompt: "select_account" });

    const result = await signInWithPopup(auth, provider);
    const user = result.user;
    const idToken = await user.getIdToken();

    return {
      user,
      idToken,
      provider: "google.com",
      providerName: "Google",
    };
  } catch (error: any) {
    if (error.code === "auth/popup-blocked") {
      throw new Error(
        "Popup-vinduet ble blokkert. Tillat popups for denne siden."
      );
    }
    if (error.code === "auth/popup-closed-by-user") {
      throw new Error("Du lukket innloggingsvinduet.");
    }
    throw error;
  }
};


/**
 * Handle redirect result (for mobile/cross-tab auth flows)
 */
export const handleRedirectResult = async (): Promise<AuthResult | null> => {
  try {
    const result = await getRedirectResult(auth);
    if (result) {
      const user = result.user;
      const idToken = await user.getIdToken();

      // Determine which provider was used
      let provider = "unknown";
      let providerName = "Unknown";

      if (result.user.providerData[0]) {
        provider = result.user.providerData[0].providerId;
        providerName =
          Object.values(OAUTH_PROVIDERS).find(
            (p) => p.id === provider
          )?.name || "Unknown";
      }

      return {
        user,
        idToken,
        provider,
        providerName,
      };
    }
    return null;
  } catch (error) {
    console.error("Redirect result error:", error);
    return null;
  }
};

/**
 * Sign out current user
 */
export const signOut = async (): Promise<void> => {
  try {
    await firebaseSignOut(auth);
  } catch (error) {
    console.error("Sign out error:", error);
    throw error;
  }
};

/**
 * Get ID token for authenticated API calls
 */
export const getIdToken = async (user: FirebaseUser): Promise<string> => {
  try {
    return await user.getIdToken();
  } catch (error) {
    console.error("Failed to get ID token:", error);
    throw error;
  }
};

/**
 * Get user's auth provider information
 */
export const getUserAuthProvider = (user: FirebaseUser): OAuthProvider | null => {
  if (user.providerData && user.providerData.length > 0) {
    const providerData = user.providerData[0];
    return Object.values(OAUTH_PROVIDERS).find(
      (p) => p.id === providerData.providerId
    ) || null;
  }
  return null;
};

/**
 * Link multiple providers to same account
 * (allows user to sign in with different provider)
 */
export const canLinkAdditionalProvider = (user: FirebaseUser): boolean => {
  return user.providerData && user.providerData.length < 3;
};

/**
 * Get list of providers currently linked to user
 */
export const getLinkedProviders = (user: FirebaseUser): string[] => {
  return user.providerData?.map((p) => p.providerId) || [];
};
